import type { UseFormReturn } from 'react-hook-form';
import {
  Labeled,
  OptionCard,
  Radio,
  RadioGroup,
  TextInputField,
} from '@sixui/ui';
import { densityTokens } from '@sixui/ui/themes/base/density.stylex';
import { scaleTokens } from '@sixui/ui/themes/base/scale.stylex';
import { spacingTokens } from '@sixui/ui/themes/base/spacing.stylex';
import stylex from '@stylexjs/stylex';
import { useTranslation } from 'react-i18next';

import type { IContainerProps } from '~/components/utils/Container';
import type { IAction } from '~/helpers/getCategoryActionFromType';
import type { IFormMode } from '~/helpers/types';
import type { IArticleFormValues } from './ArticleForm';
import { FormCheckableDisclosure } from '~/components/atoms/fields/CheckableDisclosure';
import { FormField } from '~/components/atoms/FormField';
import { ArticleSelector } from '~/components/organisms/fields/ArticleSelector';
import { CategorySelector } from '~/components/organisms/fields/CategorySelector';
import {
  categoryActionTypes,
  getCategoryActionFromType,
} from '~/helpers/getCategoryActionFromType';
import { useArticleQuery } from '~/lib/api/modules/Articles/hooks/useArticleQuery';

export type IArticleFormActionButtonSectionProps = IContainerProps & {
  mode: IFormMode;
  form: UseFormReturn<IArticleFormValues>;
  appId: string;
};

const MIN_DENSITY = -1;
const MAX_DENSITY = 0;
const DENSITY = `${densityTokens.interval} * clamp(${MIN_DENSITY}, ${densityTokens.density}, ${MAX_DENSITY}) * ${scaleTokens.scale}`;

const styles = stylex.create({
  linkTypeOptions: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gridAutoRows: '1fr',
    columnGap: spacingTokens.padding$2,
    rowGap: `calc(${spacingTokens.padding$2} + ${DENSITY})`,
  },
});

export const ArticleFormActionButtonSection: React.FC<
  IArticleFormActionButtonSectionProps
> = (props) => {
  const { sx, form, appId } = props;
  const { t } = useTranslation();

  const actionType = form.watch('actionButtonType');
  const action = actionType
    ? getCategoryActionFromType(actionType, t)
    : undefined;
  const actionTargetCategoryId = form.watch('actionButtonTargetCategoryId');
  const actionTarget = form.watch('actionButtonTarget');
  const articleId = action?.type === 'article' ? actionTarget : undefined;

  const hasActionTargetCategoryId = !!articleId && !actionTargetCategoryId;
  useArticleQuery(
    hasActionTargetCategoryId ? { appId, articleId } : undefined,
    {
      onSuccess: (data) =>
        form.setValue('actionButtonTargetCategoryId', data.categoriesId?.[0]),
    },
  );

  return (
    <FormCheckableDisclosure
      sx={sx}
      label={t('ArticleFormActionButtonSection.action-button')}
      control={form.control}
      name="actionButtonEnabled"
      initiallyExpanded={form.watch('actionButtonEnabled')}
    >
      <Labeled
        label={t('ArticleFormActionButtonSection.type')}
        disabled={!form.watch('actionButtonEnabled')}
        required
      >
        <FormField
          name="actionButtonType"
          control={form.control}
          as={RadioGroup}
          rules={{
            onChange: (event: React.ChangeEvent<HTMLSelectElement>) => {
              const actionType = event.target.value;
              const action = getCategoryActionFromType(actionType, t);
              form.setValue('actionButtonPrefix', action?.prefix ?? '');
              form.setValue('actionButtonTarget', '');
            },
          }}
          sx={styles.linkTypeOptions}
        >
          {categoryActionTypes
            .map((type) => getCategoryActionFromType(type, t))
            .filter((action): action is IAction => !!action)
            .map((action, actionIndex) => (
              <OptionCard
                key={actionIndex}
                as={Radio}
                label={action.label}
                value={action.type}
              >
                {action.supportingText}
              </OptionCard>
            ))}
        </FormField>
      </Labeled>

      {action ? (
        action.type === 'article' ? (
          <>
            <Labeled
              label={t('LinkFormMainSection.field.target-category.label')}
              disabled={!form.watch('actionButtonEnabled')}
            >
              <FormField
                name="actionButtonTargetCategoryId"
                control={form.control}
                as={CategorySelector}
                forwardError
                rules={{
                  onChange: () => form.setValue('actionButtonTarget', ''),
                }}
                appId={appId}
                filter={{ includeTypes: ['category'] }}
              />
            </Labeled>
            <Labeled
              label={t('LinkFormMainSection.field.target-article.label')}
              disabled={!form.watch('actionButtonEnabled')}
              required
            >
              <FormField
                name="actionButtonTarget"
                control={form.control}
                as={ArticleSelector}
                forwardError
                appId={appId}
                categoryId={actionTargetCategoryId}
              />
            </Labeled>
          </>
        ) : (
          <Labeled
            label={t('LinkFormMainSection.field.target.label')}
            disabled={!form.watch('actionButtonEnabled')}
            required
          >
            <FormField
              name="actionButtonTarget"
              control={form.control}
              as={TextInputField}
              forwardError
              placeholder={action?.placeholder}
              type={action?.textInputFieldType}
              autoComplete={action?.autoComplete}
            />
          </Labeled>
        )
      ) : null}
      <Labeled
        label={t('ArticleFormActionButtonSection.label')}
        disabled={!form.watch('actionButtonEnabled')}
        required
      >
        <FormField
          name="actionButtonLabel"
          control={form.control}
          as={TextInputField}
          forwardError
        />
      </Labeled>
    </FormCheckableDisclosure>
  );
};
